import { z } from "zod";

/**
 * Files (M4): photos, ID copies and supporting documents, stored in a PRIVATE
 * bucket. What both sides of the wire must agree on lives here: the kinds,
 * the document types, what may be uploaded for each kind, the limits and the
 * image rules. The database CHECKs on `files.kind` / `files.doc_type` are
 * generated from these tuples (migration 0020); a parity test holds them equal.
 *
 * Flow (PLAN §3.2): `POST /files/uploads` → the browser PUTs the bytes to a
 * presigned URL → `POST /files/complete` verifies, re-encodes and records the
 * file. Reads are presigned GETs issued after an SQL scope check; never a
 * public URL.
 */

// ── Kinds and document types ────────────────────────────────────────────────

/**
 * What a file is, which also decides who owns it:
 *   PHOTO, TV_PHOTO, SUPPORTING_DOC  belong to a CARD (`ac_card_id`)
 *   ID_FRONT, ID_BACK                belong to the PERSON (one ID copy for all their cards)
 * Later milestones add their own kinds with their own owners.
 */
export const FILE_KINDS = ["PHOTO", "TV_PHOTO", "ID_FRONT", "ID_BACK", "SUPPORTING_DOC"] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** Kinds owned by the card. */
export const CARD_FILE_KINDS = ["PHOTO", "TV_PHOTO", "SUPPORTING_DOC"] as const satisfies readonly FileKind[];
/** Kinds owned by the person (the ID copy). */
export const PERSON_FILE_KINDS = ["ID_FRONT", "ID_BACK"] as const satisfies readonly FileKind[];

export const isCardFileKind = (k: FileKind): boolean => (CARD_FILE_KINDS as readonly FileKind[]).includes(k);

/** `doc_type` of a SUPPORTING_DOC (null for every other kind). */
export const SUPPORTING_DOC_TYPES = [
	"MEDICAL_CERTIFICATE",
	"TUE",
	"PARENTAL_CONSENT",
	"PROOF_OF_ELIGIBILITY",
	"WORK_PASS",
	"MEDIA_PASS",
	"OTHER",
] as const;
export type SupportingDocType = (typeof SUPPORTING_DOC_TYPES)[number];

/**
 * Health data: readable only by admins, the registering organisation and
 * groups holding `accreditation.health` (PLAN §3.4). Everyone else is not
 * told the file exists.
 */
export const HEALTH_DOC_TYPES = ["MEDICAL_CERTIFICATE", "TUE"] as const satisfies readonly SupportingDocType[];
export const isHealthDocType = (t: string | null | undefined): boolean => t != null && (HEALTH_DOC_TYPES as readonly string[]).includes(t);

// ── Content types ───────────────────────────────────────────────────────────

export const IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const UPLOAD_CONTENT_TYPES = [...IMAGE_CONTENT_TYPES, "application/pdf"] as const;
export type UploadContentType = (typeof UPLOAD_CONTENT_TYPES)[number];

/** What is STORED: images are always re-encoded to JPEG; PDFs are kept as uploaded. */
export const STORED_CONTENT_TYPES = ["image/jpeg", "application/pdf"] as const;
export type StoredContentType = (typeof STORED_CONTENT_TYPES)[number];

/** What a kind accepts at upload. A photo is never a PDF. */
export const FILE_KIND_CONTENT_TYPES = {
	PHOTO: IMAGE_CONTENT_TYPES,
	TV_PHOTO: IMAGE_CONTENT_TYPES,
	ID_FRONT: UPLOAD_CONTENT_TYPES,
	ID_BACK: UPLOAD_CONTENT_TYPES,
	SUPPORTING_DOC: UPLOAD_CONTENT_TYPES,
} as const satisfies Record<FileKind, readonly UploadContentType[]>;

export const contentTypeAllowedFor = (kind: FileKind, contentType: string): boolean =>
	(FILE_KIND_CONTENT_TYPES[kind] as readonly string[]).includes(contentType);

/** The `accept` attribute for a kind's file input (`image/*` would hand over HEIC on iOS). */
export const fileAcceptFor = (kind: FileKind): string => FILE_KIND_CONTENT_TYPES[kind].join(",");

// ── Limits ──────────────────────────────────────────────────────────────────

/** Any upload, any kind: 15 MB (her limit). Larger → 422 FILE_TOO_LARGE. */
export const FILE_MAX_BYTES = 15 * 1024 * 1024;
/** Supporting documents per card. One more → 409 FILE_LIMIT_REACHED. */
export const SUPPORTING_DOCS_MAX = 10;
/** `original_name` is kept (sanitised) as the download name of a document. */
export const FILE_ORIGINAL_NAME_MAX = 255;

// ── Image rules (the server re-checks; it never trusts the client crop) ─────

export interface ImageRule {
	/** Required aspect ratio width / height, or null = any. */
	aspect: number | null;
	/** Allowed deviation from `aspect`, as a fraction (0.01 = ±1 %). */
	aspectTolerance: number;
	minWidth: number;
	minHeight: number;
	/** The stored display copy fits inside this box (never upscaled). */
	maxWidth: number;
	maxHeight: number;
	/** JPEG quality of the display copy. */
	quality: number;
	/** Thumbnail box (fit inside), or null = no thumbnail. */
	thumbWidth: number | null;
	thumbHeight: number | null;
}

/** Accreditation photo: 4:5 portrait, at least 480×600, stored ≤ 960×1200 q90 + a 160×200 thumb. */
export const PHOTO_ASPECT = 4 / 5;

export const IMAGE_RULES = {
	PHOTO: { aspect: PHOTO_ASPECT, aspectTolerance: 0.01, minWidth: 480, minHeight: 600, maxWidth: 960, maxHeight: 1200, quality: 90, thumbWidth: 160, thumbHeight: 200 },
	TV_PHOTO: { aspect: PHOTO_ASPECT, aspectTolerance: 0.01, minWidth: 480, minHeight: 600, maxWidth: 960, maxHeight: 1200, quality: 90, thumbWidth: 160, thumbHeight: 200 },
	// An ID copy must stay legible (a passport number): ≤ 1600 px on the long side, never letterboxed.
	ID_FRONT: { aspect: null, aspectTolerance: 0, minWidth: 1, minHeight: 1, maxWidth: 1600, maxHeight: 1600, quality: 85, thumbWidth: 320, thumbHeight: 320 },
	ID_BACK: { aspect: null, aspectTolerance: 0, minWidth: 1, minHeight: 1, maxWidth: 1600, maxHeight: 1600, quality: 85, thumbWidth: 320, thumbHeight: 320 },
	SUPPORTING_DOC: { aspect: null, aspectTolerance: 0, minWidth: 1, minHeight: 1, maxWidth: 2000, maxHeight: 2000, quality: 85, thumbWidth: 320, thumbHeight: 320 },
} as const satisfies Record<FileKind, ImageRule>;

/** Why an image was refused (`FILE_IMAGE_INVALID`, `details.reason`). */
export const FILE_IMAGE_INVALID_REASONS = ["too_small", "aspect_ratio", "unreadable"] as const;
export type FileImageInvalidReason = (typeof FILE_IMAGE_INVALID_REASONS)[number];

// ── Upload: begin ───────────────────────────────────────────────────────────

/**
 * `POST /files/uploads` — ask to upload one file. Nothing is written to the
 * database: the answer is a presigned PUT plus a signed upload token.
 *
 * `ac_card_id` is always the card the upload is made from; for an ID copy it
 * names the card through which the person is reached (the write check runs
 * against that card), and the file is filed under the person.
 */
export const beginUploadRequest = z
	.object({
		kind: z.enum(FILE_KINDS),
		ac_card_id: z.string().uuid(),
		doc_type: z.enum(SUPPORTING_DOC_TYPES).nullish(),
		/** Checked against the kind by the service → 422 FILE_TYPE_NOT_ALLOWED (not a validation error). */
		content_type: z.string().trim().toLowerCase().min(1).max(100),
		/** Checked against FILE_MAX_BYTES by the service → 422 FILE_TOO_LARGE with `details.max_bytes`. */
		bytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
		original_name: z.string().trim().min(1).max(FILE_ORIGINAL_NAME_MAX).nullish(),
	})
	.refine((b) => (b.kind === "SUPPORTING_DOC") === (b.doc_type != null), {
		path: ["doc_type"],
		message: "A supporting document needs a document type; other files have none",
	});
export type BeginUploadBody = z.input<typeof beginUploadRequest>;

export interface BeginUploadResponse {
	file_id: string;
	/** Presigned PUT, valid for `FILE_UPLOAD_URL_TTL_SECONDS`. A credential: never log it. */
	upload_url: string;
	/** Send exactly these headers with the PUT (they are signed). */
	upload_headers: { "content-type": string };
	/** Hand back to `POST /files/complete`. */
	upload_token: string;
	expires_at: string;
}

// ── Upload: complete ────────────────────────────────────────────────────────

export const completeUploadRequest = z.object({
	upload_token: z.string().min(1).max(4096),
});
export type CompleteUploadBody = z.input<typeof completeUploadRequest>;

// ── Reads ───────────────────────────────────────────────────────────────────

/** `GET /files/:id/url` — fresh presigned URLs (lightbox, PDF open, an expired record URL). */
export interface FileUrlResponse {
	url: string;
	thumb_url: string | null;
	expires_at: string;
}

/** Languages of the ID-scan OCR, as Tesseract names them. */
export const OCR_LANGS = ["eng", "mrz"] as const;
export type OcrLang = (typeof OCR_LANGS)[number];

/** `GET /files/ocr-assets` — presigned GETs for the gzipped traineddata, one per language. */
export interface OcrAssetsResponse {
	traineddata: Record<OcrLang, string>;
	expires_at: string;
}
